import { createFileRoute, Link, useParams, useRouter } from "@tanstack/react-router"
import { Header } from "react-aria-components"
import { useTranslation } from "react-i18next"

import Heading from "@/globalComponents/atoms/Heading"
import NotTranslatable from "@/globalComponents/atoms/NotTranslatable"
import { formatZonedDateTimeWithTimeZone } from "@/utilities/etc"
import { POLICIES_UPDATED_DATE } from "@/utilities/globals"

export const Route = createFileRoute("/terms-of-use")({
  staticData: {
    pageName: "Terms of Use",
  },
  loader: ({ context }) => {
    context.setDocumentThemeMode("root")
  },
  component: TermsOfUseRouteComponent,
})

export function TermsOfUseRouteComponent() {
  const { t } = useTranslation()
  const router = useRouter()
  const { qrLookupID } = useParams({ strict: false })
  const privacyPolicyLink = `${window.location.origin}${
    router.buildLocation(
      qrLookupID
        ? { to: "/qr/$qrLookupID/privacy-policy", params: { qrLookupID } }
        : { to: "/privacy-policy" },
    ).href
  }`

  return (
    <div className="legal">
      <Header>
        <Heading level={1}>{t("termsOfUse.header")}</Heading>
        <p>
          {t("privacyPolicy.updatedAt", {
            date: formatZonedDateTimeWithTimeZone(POLICIES_UPDATED_DATE),
          })}
        </p>
        <NotTranslatable />
      </Header>
      <Heading level={2} id="acceptance-of-the-terms-of-use">
        <Link to="." hash="acceptance-of-the-terms-of-use">
          Acceptance of the Terms of Use
        </Link>
      </Heading>
      <p>
        These terms of use are entered into by and between You (“You” or “Your”)
        and Inflow Inc. (“Company,” “we,” or “us”). The following terms and
        conditions, together with any documents they expressly incorporate by
        reference (collectively, “Terms of Use”), govern Your access to and use
        of <a href={window.location.origin}>{window.location.hostname}</a>,
        including any content, functionality, and services (the “Website”),
        whether as a guest or a registered user.
      </p>
      <p>
        Please read the Terms of Use carefully before You start to use the
        Website AND SERVICES AS THEY CONTAIN AN AGREEMENT TO ARBITRATE, CLASS
        ACTION WAIVER, AND OTHER IMPORTANT INFORMATION REGARDING YOUR LEGAL
        RIGHTS, REMEDIES, AND OBLIGATIONS. THE AGREEMENT TO ARBITRATE REQUIRES
        (WITH LIMITED EXCEPTION) THAT YOU SUBMIT CLAIMS YOU HAVE AGAINST US TO
        BINDING AND FINAL ARBITRATION, AND FURTHER (1) YOU WILL ONLY BE
        PERMITTED TO PURSUE CLAIMS AGAINST THE COMPANY ON AN INDIVIDUAL BASIS,
        NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY CLASS OR REPRESENTATIVE ACTION
        OR PROCEEDING, (2) YOU WILL ONLY BE PERMITTED TO SEEK RELIEF (INCLUDING
        MONETARY, INJUNCTIVE, AND DECLARATORY RELIEF) ON AN INDIVIDUAL BASIS,
        AND (3) YOU MAY NOT BE ABLE TO HAVE ANY CLAIMS YOU HAVE AGAINST US
        RESOLVED BY A JURY OR IN A COURT OF LAW. YOU CAN OPT OUT OF THIS
        AGREEMENT TO ARBITRATE BY CONTACTING LEGAL@INFLOWSYSTEMS.COM WITHIN 30
        DAYS AFTER FIRST ACCEPTING THESE TERMS OF SERVICE AND STATING YOUR FIRST
        AND LAST NAME AND THAT YOU DECLINE THE TERMS TO ARBITRATE. By using the
        Website and Services, You accept and agree to be bound and abide by
        these Terms of Use and our Privacy Policy, (accessible at{" "}
        {qrLookupID ? (
            <Link
              to="/qr/$qrLookupID/privacy-policy"
              params={{ qrLookupID }}
            >
              {privacyPolicyLink}
            </Link>
          ) : (
            <Link to="/privacy-policy">{privacyPolicyLink}</Link>
          )}), incorporated
        herein by reference. If You do not want to agree to these Terms of Use
        or the Privacy Policy, You must not access or use the Website or
        Services.
      </p>
      <p>
        By using this Website and Services, You represent and warrant that You
        are of legal age to form a binding contract with the Company and meet
        all of the foregoing eligibility requirements. Any use or access by
        anyone under age 13 is prohibited. If You do not meet all of these
        requirements, You must not access or use the Website. If You fail to
        comply with these Terms of Use, Your permission to use the Website
        automatically terminates.
      </p>
      <Heading level={2} id="changes-to-the-terms-of-use">
        <Link to="." hash="changes-to-the-terms-of-use">
          Changes to the Terms of Use
        </Link>
      </Heading>
      <p>
        We may revise and update these Terms of Use from time to time in our
        sole discretion. All changes are effective immediately when we post
        them, and apply to all access to and use of the Website thereafter.
      </p>
      <p>
        Your continued use of the Website following the posting of revised Terms
        of Use means that You accept and agree to the changes. You are expected
        to check this page from time to time You access this Website so You are
        aware of any changes, as they are binding on You.
      </p>
      <Heading level={2} id="services">
        <Link to="." hash="services">
          Services
        </Link>
      </Heading>
      <p>
        The services provided by the Company allow You to book or reserve a
        parking spot at selected parking facilities across various cities using
        an application or other technology to book, reserve, and/or pay for use
        of the parking spot (“Services”). You can reserve a parking spot by
        scanning a QR code associated with a particular parking facility that
        will direct You to our Website to enter reservation details and payment
        information. Some parking facilities may assess a fee for any reserved
        parking spot where You never arrive to utilize the spot, including if
        You hold a season pass or similar privilege for a particular parking
        facility. Specific information about fees for un-utilized reserved or
        prepaid Services will be presented at the point of sale for the
        transaction.
      </p>
      <Heading level={2} id="payments-and-fees">
        <Link to="." hash="payments-and-fees">
          Payments and Fees
        </Link>
      </Heading>
      <p>
        You understand that use of the Services may result in charges to You for
        the Services You receive, Services You reserve but to not use, and for
        use of the application ("Charges"). We will receive and/or enable Your
        payment of the applicable Charges for services obtained through Your use
        of the Services. Charges will be inclusive of applicable taxes where
        required by law. Charges may include other applicable fees, processing
        fees, or a convenience fee for use of the application in relation to the
        Services. The convenience fee will be assessed on a per-transaction
        basis for use of the application and disclosed to You at the time of the
        transaction.
      </p>
      <p>
        All Charges and payments will be enabled by the Company using the
        preferred payment provided by You, after which You will receive a
        receipt by text and/or email. You represent and warrant that You are
        authorized to use the payment method provided by You and that the
        payment information is accurate. If the payment method provided by You
        is determined to be expired, invalid or otherwise not able to be
        charged, Your request to use the Services will be declined and You will
        be notified by text and/or email.{" "}
        <em>
          Charges paid by You are final and non-refundable, including but not
          limited to Services that You reserve or prepay for that You do not
          utilize, unless otherwise determined by the Company.
        </em>
      </p>
      <p>
        You understand that should your Services expire while You are still
        using a parking spot, all penalties and costs for parking without
        payment applicable to the parking location will apply. This includes but
        is not limited to parking violations and related late fees for
        non-payment. As between You and the Company, the Company reserves the
        right to establish, remove and/or revise Charges for any or all services
        obtained through the use of the Services at any time in our sole
        discretion. We will use reasonable efforts to inform You of Charges that
        may apply, provided that You will be responsible for Charges incurred by
        You regardless of Your awareness of such Charges or the amounts thereof.
        We may from time to time provide certain users with promotional offers
        and discounts that may result in different amounts charged for the same
        or similar services or goods obtained through the use of the Services,
        and You agree that such promotional offers and discounts, unless also
        made available to You, shall have no bearing on Your use of the Services
        or the Charges applied to You.
      </p>
      <p>
        In certain cases, with respect to third party providers, Charges You
        incur will be owed directly to third party providers, and the Company
        will collect payment of those charges from You, on the third party
        provider's behalf as their limited payment collection agent, and payment
        of the Charges shall be considered the same as payment made directly by
        You to the third party provider.
      </p>
      <Heading level={2} id="communications">
        <Link to="." hash="communications">
          Communications
        </Link>
      </Heading>
      <p>
        By accepting these Terms of Use, You expressly consent to the Company,
        either directly or through third parties acting on the Company’s behalf,
        to communicate with You via your mobile device and by email. This
        includes sending informational texts such as messages that your parking
        session is about to expire, marketing, and promotional messages,
        including SMS, MMS, or text messages and calls made using an automatic
        telephone dialing system or by pre-recorded or artificial message
        related to the Services. Such messages may be made to any telephone
        number that You provide to the Company. Accepting this consent is not
        required to obtain any good or service. You also consent to the Company
        communicating by e-mail to the email address provided by You in relation
        to the use of the Website or Services.
      </p>
      <p>
        <em>Short Code Texting</em>. In addition to the texting described above,
        the Company also offers the ability to pay by text using “short code”
        texting (“<em>SMS Service</em>”) where You may text the Company using a
        code posted at one of the Company’s parking facilities. By texting a
        keyword to <a href="sms:94201">94201</a>, You consent to receive one or
        more automated text messages at the phone number from which You texted.
        Message and Data Rates may apply.
      </p>
      <p>
        The Company will not be liable for any delays in the receipt of any SMS
        messages as delivery is subject to effective transmission from Your
        mobile service operator. THE SMS SERVICE IS PROVIDED ON AN “AS IS”
        BASIS, AND WE MAKE NO WARRANTY, EXPRESS OR IMPLIED, AND ALL WARRANTIES,
        INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR
        PARTICULAR PURPOSE, ARE HEREBY EXPRESSLY DISCLAIMED.
      </p>
      <p>
        Data obtained from You in connection with this SMS Service may include
        Your cell phone number, Your carrier’s name, and the date, time and
        content of your messages, as well as other information that you provide.
        We may use this information to contact you and to provide the services
        you request from us. By subscribing, You consent to receive up to four
        text messages per month utilizing the SMS Service. By subscribing or
        otherwise using the service, you acknowledge and agree that we will have
        the right to change and/or terminate the service at any time, with or
        without cause and/or advance notice.
      </p>
      <p>
        To cancel your SMS subscriptions, text{" "}
        <a href="sms:94201;?body=STOP">STOP to 94201</a> in reply to a text
        message You receive. You may receive a subsequent message confirming
        your opt-out request.
      </p>
      <p>
        T-Mobile® is not liable for delayed or undelivered messages. United
        States Participating Carriers Include: AT&T®, T-Mobile®, Verizon
        Wireless®, Sprint®, Boost®, U.S. Cellular®, MetroPCS®, InterOp®,
        Cellcom®, C Spire Wireless®, Cricket®, Virgin Mobile® and others.
      </p>
      <p>
        The SMS Service is only available to residents of the United States. You
        understand and acknowledge that You may not sign up for, access, or
        attempt to access or use the SMS Service from countries outside of the
        U.S. You agree to abide by U.S. and other applicable export control laws
        and not to transfer, by electronic transmission or otherwise, any
        content or software subject to restrictions under such laws to a
        national destination or person prohibited under such laws.
      </p>
      <p>
        For questions or support relative to the SMS Service, You can contact us
        at <a href="mailto:info@inflowsystems.com">info@inflowsystems.com</a> at
        any time, from Your mobile phone, send a text message with the word{" "}
        <a href="sms:94201;?body=HELP">“HELP” to 94201</a>. You may also call
        the Company’s support line:{" "}
        <a href="tel:+14142938625">(414) 293-8625</a>.
      </p>
      <Heading level={2} id="accessing-the-website-and-account-security">
        <Link to="." hash="accessing-the-website-and-account-security">
          Accessing the Website and Account Security
        </Link>
      </Heading>
      <p>
        We reserve the right to withdraw or amend this Website, and any service
        or material we provide on the Website, in our sole discretion without
        notice. We will not be liable if, for any reason, all or any part of the
        Website is unavailable at any time or for any period. From time to time,
        we may restrict access to some parts of the Website, or the entire
        Website, to users, including registered users.
      </p>
      <p>You are responsible for both:</p>
      <ul>
        <li>
          Making all arrangements necessary for You to have access to the
          Website.
        </li>
        <li>
          Ensuring that all persons who access the Website through Your internet
          connection or systems are aware of these Terms of Use and comply with
          them.
        </li>
      </ul>
      <p>
        To access the Website or some of the resources it offers, You may be
        asked to provide certain registration details or other information. It
        is a condition of Your use of the Website that all the information You
        provide on the Website is correct, current, and complete. You agree that
        all information You provide to register with this Website or otherwise,
        including, but not limited to, through the use of any interactive
        features on the Website, is governed by our Privacy Policy (accessible
        at {qrLookupID ? (
            <Link
              to="/qr/$qrLookupID/privacy-policy"
              params={{ qrLookupID }}
            >
              {privacyPolicyLink}
            </Link>
          ) : (
            <Link to="/privacy-policy">{privacyPolicyLink}</Link>
          )}), and You
        consent to all actions we take with respect to Your information
        consistent with our Privacy Policy.
      </p>
      <p>
        If You choose, or are provided with, a user name, password, or any other
        piece of information as part of our security procedures, You must treat
        such information as confidential, and You must not disclose it to any
        other person or entity. You also acknowledge that Your account is
        personal to You and agree not to provide any other person with access to
        this Website or portions of it using Your user name, password, or other
        security information. You agree to notify us immediately of any
        unauthorized access to or use of Your user name or password or any other
        breach of security. You also agree to ensure that You exit from Your
        account at the end of each session. You should use particular caution
        when accessing Your account from a public or shared computer so that
        others are not able to view or record Your password or other personal
        information.
      </p>
      <p>
        We have the right to disable any user name, password, or other
        identifier, whether chosen by You or provided by us, at any time in our
        sole discretion for any or no reason, including if, in our opinion, You
        have violated any provision of these Terms of Use.
      </p>
      <Heading level={2} id="intellectual-property-rights">
        <Link to="." hash="intellectual-property-rights">
          Intellectual Property Rights
        </Link>
      </Heading>
      <p>
        The Website and its entire contents, features, and functionality
        (including but not limited to all information, software, text, displays,
        images, video, and audio, and the design, selection, and arrangement
        thereof) are owned by the Company, its licensors, or other providers of
        such material and are protected by United States and international
        copyright, trademark, patent, trade secret, and other intellectual
        property or proprietary rights laws.
      </p>
      <p>
        These Terms of Use permit You to use the Website for Your personal,
        non-commercial use only. You must not reproduce, distribute, modify,
        create derivative works of, publicly display, publicly perform,
        republish, download, store, or transmit any of the material on our
        Website, except as follows:
      </p>
      <ul>
        <li>
          Your computer may temporarily store copies of such materials in RAM
          incidental to Your accessing and viewing those materials.
        </li>
        <li>
          You may store files that are automatically cached by Your Web browser
          for display enhancement purposes.
        </li>
        <li>
          You may print or download one copy of a reasonable number of pages of
          the Website for Your own personal, non-commercial use and not for
          further reproduction, publication, or distribution.
        </li>
        <li>
          If we provide desktop, mobile, or other applications for download, You
          may download a single copy to Your computer or mobile device solely
          for Your own personal, non-commercial use, provided You agree to be
          bound by our end user license agreement for such applications.
        </li>
      </ul>
      <p>You must not:</p>
      <ul>
        <li>Modify copies of any materials from this site.</li>
        <li>
          Use any illustrations, photographs, video or audio sequences, or any
          graphics separately from the accompanying text.
        </li>
        <li>
          Delete or alter any copyright, trademark, or other proprietary rights
          notices from copies of materials from this site.
        </li>
      </ul>
      <p>
        If You print, copy, modify, download, or otherwise use or provide any
        other person with access to any part of the Website in breach of the
        Terms of Use, Your right to use the Website will stop immediately and
        You must, at our option, return or destroy any copies of the materials
        You have made. No right, title, or interest in or to the Website or any
        content on the Website is transferred to You, and all rights not
        expressly granted are reserved by the Company. Any use of the Website
        not expressly permitted by these Terms of Use is a breach of these Terms
        of Use and may violate copyright, trademark, and other laws.
      </p>
      <Heading level={2} id="trademarks">
        <Link to="." hash="trademarks">
          Trademarks
        </Link>
      </Heading>
      <p>
        The Company name, the Company logo, and all related names, logos,
        product and service names, designs, and slogans are trademarks of the
        Company or its affiliates or licensors. You must not use such marks
        without the prior written permission of the Company. All other names,
        logos, product and service names, designs, and slogans on this Website
        are the trademarks of their respective owners.
      </p>
      <Heading level={2} id="prohibited-uses">
        <Link to="." hash="prohibited-uses">
          Prohibited Uses
        </Link>
      </Heading>
      <p>
        You may use the Website only for lawful purposes and in accordance with
        these Terms of Use. You agree not to use the Website:
      </p>
      <ul>
        <li>
          In any way that violates any applicable federal, state, local, or
          international law or regulation (including, without limitation, any
          laws regarding the export of data or software to and from the US or
          other countries).
        </li>
        <li>
          For the purpose of exploiting, harming, or attempting to exploit or
          harm minors in any way by exposing them to inappropriate content,
          asking for personally identifiable information, or otherwise.
        </li>
        <li>
          To transmit, or procure the sending of, any advertising or promotional
          material without our prior written consent, including any “junk mail,”
          “chain letter,” “spam,” or any other similar solicitation.
        </li>
        <li>
          To impersonate or attempt to impersonate the Company, a Company
          employee, another user, or any other person or entity (including,
          without limitation, by using email addresses or screen names
          associated with any of the foregoing).
        </li>
        <li>
          To engage in any other conduct that restricts or inhibits anyone’s use
          or enjoyment of the Website, or which, as determined by us, may harm
          the Company or users of the Website, or expose them to liability.
        </li>
      </ul>
      <p>Additionally, You agree not to:</p>
      <ul>
        <li>
          Use the Website in any manner that could disable, overburden, damage,
          or impair the site or interfere with any other party’s use of the
          Website, including their ability to engage in real time activities
          through the Website.
        </li>
        <li>
          Use any robot, spider, or other automatic device, process, or means to
          access the Website for any purpose, including monitoring or copying
          any of the material on the Website.
        </li>
        <li>
          Use any manual process to monitor or copy any of the material on the
          Website, or for any other purpose not expressly authorized in these
          Terms of Use, without our prior written consent.
        </li>
        <li>
          Use any device, software, or routine that interferes with the proper
          working of the Website.
        </li>
        <li>
          Introduce any viruses, Trojan horses, worms, logic bombs, or other
          material that is malicious or technologically harmful.
        </li>
        <li>
          Attempt to gain unauthorized access to, interfere with, damage, or
          disrupt any parts of the Website, the server on which the Website is
          stored, or any server, computer, or database connected to the Website.
        </li>
        <li>
          Attack the Website via a denial-of-service attack or a distributed
          denial-of-service attack.
        </li>
        <li>
          Otherwise attempt to interfere with the proper working of the Website.
        </li>
      </ul>
      <Heading level={2} id="reliance-on-information-posted">
        <Link to="." hash="reliance-on-information-posted">
          Reliance on Information Posted
        </Link>
      </Heading>
      <p>
        The information presented on or through the Website is made available
        solely for general information purposes. We do not warrant the accuracy,
        completeness, or usefulness of this information. Any reliance You place
        on such information is strictly at Your own risk. We disclaim all
        liability and responsibility arising from any reliance placed on such
        materials by You or any other visitor to the Website, or by anyone who
        may be informed of any of its contents.
      </p>
      <p>
        This Website may include content provided by third parties, including
        materials provided by other users, and third-party licensors,
        syndicators, aggregators, and/or reporting services. All statements
        and/or opinions expressed in these materials, and all articles and
        responses to questions and other content, other than the content
        provided by the Company, are solely the opinions and the responsibility
        of the person or entity providing those materials. These materials do
        not necessarily reflect the opinion of the Company. We are not
        responsible, or liable to You or any third party, for the content or
        accuracy of any materials provided by any third parties.
      </p>
      <Heading level={2} id="changes-to-the-website">
        <Link to="." hash="changes-to-the-website">
          Changes to the Website
        </Link>
      </Heading>
      <p>
        We may update the content on this Website from time to time, but its
        content is not necessarily complete or up-to-date. Any of the material
        on the Website may be out of date at any given time, and we are under no
        obligation to update such material.
      </p>
      <Heading level={2} id="information-about-you">
        <Link to="." hash="information-about-you">
          Information About You and Your Visits to the Website
        </Link>
      </Heading>
      <p>
        All information we collect on this Website is subject to our Privacy
        Policy (accessible at{" "}
        {qrLookupID ? (
            <Link
              to="/qr/$qrLookupID/privacy-policy"
              params={{ qrLookupID }}
            >
              {privacyPolicyLink}
            </Link>
          ) : (
            <Link to="/privacy-policy">{privacyPolicyLink}</Link>
          )}). By using the
        Website, You consent to all actions taken by us with respect to Your
        information in compliance with the Privacy Policy.
      </p>
      <Heading level={2} id="linking-to-the-website">
        <Link to="." hash="linking-to-the-website">
          Linking to the Website
        </Link>
      </Heading>
      <p>
        You may link to our homepage, provided You do so in a way that is fair
        and legal and does not damage our reputation or take advantage of it,
        but You must not establish a link in such a way as to suggest any form
        of association, approval, or endorsement on our part without our express
        written consent.
      </p>
      <Heading level={2} id="links-from-the-website">
        <Link to="." hash="links-from-the-website">
          Links from the Website
        </Link>
      </Heading>
      <p>
        If the Website contains links to other sites and resources provided by
        third parties, these links are provided for Your convenience only. This
        includes links contained in advertisements, including banner
        advertisements and sponsored links. We have no control over the contents
        of those sites or resources, and accept no responsibility for them or
        for any loss or damage that may arise from Your use of them. If You
        decide to access any of the third-party websites linked to this Website,
        You do so entirely at Your own risk and subject to the terms and
        conditions of use for such websites.
      </p>
      <Heading level={2} id="geographic-restrictions">
        <Link to="." hash="geographic-restrictions">
          Geographic Restrictions
        </Link>
      </Heading>
      <p>
        The owner of the Website is based in the State of Wisconsin in the
        United States. We provide this Website for use only by persons located
        in the United States. We make no claims that the Website or any of its
        content is accessible or appropriate outside of the United States.
        Access to the Website may not be legal by certain persons or in certain
        countries. If You access the Website from outside the United States, You
        do so on Your own initiative and are responsible for compliance with
        local laws.
      </p>
      <Heading level={2} id="disclaimer-of-warranties">
        <Link to="." hash="disclaimer-of-warranties">
          Disclaimer of Warranties
        </Link>
      </Heading>
      <p>
        You understand that we cannot and do not guarantee or warrant that files
        available for downloading from the internet or the Website will be free
        of viruses or other destructive code. You are responsible for
        implementing sufficient procedures and checkpoints to satisfy Your
        particular requirements for anti-virus protection and accuracy of data
        input and output, and for maintaining a means external to our site for
        any reconstruction of any lost data. TO THE FULLEST EXTENT PROVIDED BY
        LAW, WE WILL NOT BE LIABLE FOR ANY LOSS OR DAMAGE CAUSED BY A
        DISTRIBUTED DENIAL-OF-SERVICE ATTACK, VIRUSES, OR OTHER TECHNOLOGICALLY
        HARMFUL MATERIAL THAT MAY INFECT YOUR COMPUTER EQUIPMENT, COMPUTER
        PROGRAMS, DATA, OR OTHER PROPRIETARY MATERIAL DUE TO YOUR USE OF THE
        WEBSITE OR ANY SERVICES OR ITEMS OBTAINED THROUGH THE WEBSITE OR TO YOUR
        DOWNLOADING OF ANY MATERIAL POSTED ON IT, OR ON ANY WEBSITE LINKED TO
        IT.
      </p>
      <p>
        YOUR USE OF THE WEBSITE, ITS CONTENT, AND ANY SERVICES OR ITEMS OBTAINED
        THROUGH THE WEBSITE IS AT YOUR OWN RISK. THE WEBSITE, ITS CONTENT, AND
        ANY SERVICES OR ITEMS OBTAINED THROUGH THE WEBSITE ARE PROVIDED ON AN
        “AS IS” AND “AS AVAILABLE” BASIS, WITHOUT ANY WARRANTIES OF ANY KIND,
        EITHER EXPRESS OR IMPLIED. NEITHER THE COMPANY NOR ANY PERSON ASSOCIATED
        WITH THE COMPANY MAKES ANY WARRANTY OR REPRESENTATION WITH RESPECT TO
        THE COMPLETENESS, SECURITY, RELIABILITY, QUALITY, ACCURACY, OR
        AVAILABILITY OF THE WEBSITE. WITHOUT LIMITING THE FOREGOING, NEITHER THE
        COMPANY NOR ANYONE ASSOCIATED WITH THE COMPANY REPRESENTS OR WARRANTS
        THAT THE WEBSITE, ITS CONTENT, OR ANY SERVICES OR ITEMS OBTAINED THROUGH
        THE WEBSITE WILL BE ACCURATE, RELIABLE, ERROR-FREE, OR UNINTERRUPTED,
        THAT DEFECTS WILL BE CORRECTED, THAT OUR SITE OR THE SERVER THAT MAKES
        IT AVAILABLE ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS, OR THAT
        THE WEBSITE OR ANY SERVICES OR ITEMS OBTAINED THROUGH THE WEBSITE WILL
        OTHERWISE MEET YOUR NEEDS OR EXPECTATIONS. NO ADVICE OR INFORMATION,
        WHETHER ORAL OR WRITTEN, OBTAINED BY YOU FROM THE COMPANY OR THROUGH OR
        FROM THE SERVICES SHALL CREATE ANY WARRANTY NOT EXPRESSLY STATED IN THE
        TERMS AND CONDITIONS.
      </p>
      <p>
        TO THE FULLEST EXTENT PROVIDED BY LAW, THE COMPANY HEREBY DISCLAIMS ALL
        WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, STATUTORY, OR
        OTHERWISE, INCLUDING BUT NOT LIMITED TO ANY WARRANTIES OF
        MERCHANTABILITY, NON-INFRINGEMENT, AND FITNESS FOR PARTICULAR PURPOSE.
      </p>
      <p>
        THE FOREGOING DOES NOT AFFECT ANY WARRANTIES THAT CANNOT BE EXCLUDED OR
        LIMITED UNDER APPLICABLE LAW.
      </p>
      <Heading level={2} id="limitation-on-liability">
        <Link to="." hash="limitation-on-liability">
          Limitation on Liability
        </Link>
      </Heading>
      <p>
        TO THE FULLEST EXTENT PROVIDED BY LAW, IN NO EVENT WILL THE COMPANY, ITS
        AFFILIATES, OR THEIR LICENSORS, SERVICE PROVIDERS, EMPLOYEES, AGENTS,
        OFFICERS, OR DIRECTORS BE LIABLE FOR DAMAGES OF ANY KIND, UNDER ANY
        LEGAL THEORY, ARISING OUT OF OR IN CONNECTION WITH YOUR USE, OR
        INABILITY TO USE, THE WEBSITE, ANY WEBSITES LINKED TO IT, ANY CONTENT ON
        THE WEBSITE OR SUCH OTHER WEBSITES, INCLUDING ANY DIRECT, INDIRECT,
        SPECIAL, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT
        NOT LIMITED TO, PERSONAL INJURY, PAIN AND SUFFERING, EMOTIONAL DISTRESS,
        LOSS OF REVENUE, LOSS OF PROFITS, LOSS OF BUSINESS OR ANTICIPATED
        SAVINGS, LOSS OF USE, LOSS OF GOODWILL, LOSS OF DATA, AND WHETHER CAUSED
        BY TORT (INCLUDING NEGLIGENCE), BREACH OF CONTRACT, OR OTHERWISE, EVEN
        IF FORESEEABLE, EXCEED THE AMOUNT YOU HAVE PAID TO THE COMPANY FOR THE
        APPLICABLE SERVICE IN THE LAST TWELVE (12) MONTHS OUT OF WHICH LIABILITY
        AROSE.
      </p>
      <p>
        THE FOREGOING DOES NOT AFFECT ANY LIABILITY THAT CANNOT BE EXCLUDED OR
        LIMITED UNDER APPLICABLE LAW.
      </p>
      <Heading level={2} id="personal-injury-or-death">
        <Link to="." hash="personal-injury-or-death">
          Personal Injury or Death
        </Link>
      </Heading>
      <p>
        You acknowledge that motor vehicle accidents can be dangerous and that
        collision with other motor vehicles or with other objects can cause
        injury or death to You or a third party. YOU EXPRESSLY UNDERSTAND AND
        AGREE THAT NEITHER THE COMPANY NOR ANY OF ITS SUBSIDIARIES, DIVISIONS,
        AFFILIATES, AGENTS, PARTNERS, LICENSEES, OR LICENSORS SHALL BE LIABLE TO
        YOU OR ANYONE ELSE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
        CONSEQUENTIAL OR EXEMPLARY DAMAGES, INCLUDING BUT NOT LIMITED TO,
        DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA OR OTHER INTANGIBLE
        LOSSES (EVEN IF THE COMPANY HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH
        DAMAGES), RESULTING FROM THE DESTRUCTION OR LOSS OF PROPERTY, PERSONAL
        INJURY OR DEATH RELATED IN ANY WAY TO THE USE OF THE SERVICES.
      </p>
      <Heading level={2} id="indemnification">
        <Link to="." hash="indemnification">
          Indemnification
        </Link>
      </Heading>
      <p>
        You agree to defend, indemnify, and hold harmless the Company, its
        affiliates, licensors, and service providers, and its and their
        respective officers, directors, employees, contractors, agents,
        licensors, suppliers, successors, and assigns from and against any
        claims, liabilities, damages, judgments, awards, losses, costs,
        expenses, or fees (including reasonable attorneys’ fees) arising out of
        or relating to (a) Your violation of these Terms of Use or Your use of
        the Website, including, but not limited to, any use of the Website’s
        content, services, and products other than as expressly authorized in
        these Terms of Use, or Your use of any information obtained from the
        Website; and (b) Your use or reliance on, or publication, communication
        or distribution of anything on or from the Website.
      </p>
      <Heading level={2} id="governing-law-and-jurisdiction">
        <Link to="." hash="governing-law-and-jurisdiction">
          Governing Law and Jurisdiction
        </Link>
      </Heading>
      <p>
        All matters relating to the Website and these Terms of Use, and any
        dispute or claim arising therefrom or related thereto (in each case,
        including non-contractual disputes or claims), shall be governed by and
        construed in accordance with the internal laws of the State of Wisconsin
        without giving effect to any choice or conflict of law provision or rule
        (whether of the State of Wisconsin or any other jurisdiction).
      </p>
      <p>
        Any legal suit, action, or proceeding arising out of, or related to,
        these Terms of Use or the Website shall be instituted exclusively in the
        federal courts of the United States or the courts of the State of
        Wisconsin, in each case located in the City of Milwaukee and County of
        Milwaukee, although we retain the right to bring any suit, action, or
        proceeding against You for breach of these Terms of Use in Your country
        of residence or any other relevant country. You waive any and all
        objections to the exercise of jurisdiction over You by such courts and
        to venue in such courts.
      </p>
      <Heading
        level={2}
        id="dispute-resolution-by-binding-arbitration-and-class-action-waiver"
      >
        <Link
          to="."
          hash="dispute-resolution-by-binding-arbitration-and-class-action-waiver"
        >
          Dispute Resolution by Binding Arbitration and Class Action Waiver
        </Link>
      </Heading>
      <p>
        <strong>
          PLEASE READ THIS SECTION CAREFULLY AS IT AFFECTS YOUR LEGAL RIGHTS.
        </strong>
      </p>
      <p>
        This Dispute Resolution by Binding Arbitration and Class Action Waiver
        section is referred to in this Terms of Service as the “Arbitration
        Agreement.”
      </p>
      <p>
        The parties to this Agreement specifically agree that this transaction
        involves interstate commerce and that any Dispute (as hereinafter
        defined) shall first be submitted to mediation and, if not settled
        during mediation, shall thereafter be submitted to binding arbitration
        as provided by the Federal Arbitration Act (9 U.S.C. §§1 et seq.) and
        not by or in a court of law or equity. "Disputes" (whether contract,
        warranty, tort, statutory or otherwise), shall include, but are not
        limited to, any and all controversies, disputes or claims (1) arising
        under, or related to, this Agreement and the services provided to You
        under these terms of use; (2) relating to personal injury or property
        damage alleged to have been sustained by You when parking or otherwise
        purchasing services under these terms of use; or (3) issues of
        formation, validity or enforceability of this Agreement. You have
        executed this Agreement on behalf of with the intent to be bound hereby.
        Any Dispute shall be submitted for binding arbitration within a
        reasonable time after such Dispute has arisen. Nothing herein shall
        extend the time period by which a claim or cause of action may be
        asserted under the applicable statute of limitations or statute of
        repose, and in no event shall the Dispute be submitted for arbitration
        after the date when institution of a legal or equitable proceeding based
        on the underlying claims in such Dispute would be barred by the
        applicable statute of limitations or statute of repose.
      </p>
      <Heading level={3}>
        Prohibition of Class and Representative Actions and Non-Individualized
        Relief
      </Heading>
      <p>
        YOU AND THE COMPANY AGREE THAT EACH OF US MAY BRING CLAIMS AGAINST THE
        OTHER ONLY ON AN INDIVIDUAL BASIS AND NOT AS A PLAINTIFF OR CLASS MEMBER
        IN ANY PURPORTED CLASS OR REPRESENTATIVE ACTION OR PROCEEDING. UNLESS
        BOTH YOU AND THE COMPANY AGREE OTHERWISE, THE ARBITRATOR MAY NOT
        CONSOLIDATE OR JOIN MORE THAN ONE PERSON’S OR PARTY’S CLAIMS AND MAY NOT
        OTHERWISE PRESIDE OVER ANY FORM OF A CONSOLIDATED, REPRESENTATIVE, OR
        CLASS PROCEEDING. ALSO, THE ARBITRATOR MAY AWARD RELIEF (INCLUDING
        MONETARY, INJUNCTIVE, AND DECLARATORY RELIEF) ONLY IN FAVOR OF THE
        INDIVIDUAL PARTY SEEKING RELIEF AND ONLY TO THE EXTENT NECESSARY TO
        PROVIDE RELIEF NECESSITATED BY THAT PARTY’S INDIVIDUAL CLAIM(S), EXCEPT
        THAT YOU MAY PURSUE A CLAIM FOR AND THE ARBITRATOR MAY AWARD PUBLIC
        INJUNCTIVE RELIEF UNDER APPLICABLE LAW TO THE EXTENT REQUIRED FOR THE
        ENFORCEABILITY OF THIS PROVISION.
      </p>
      <Heading level={3}>Mediation and Arbitration Rules</Heading>
      <p>
        Any and all mediations commenced by any of the parties to this Agreement
        shall be filed with and administered by the American Arbitration
        Association or any successor thereto ("AAA") in accordance with the
        AAA's Consumer Arbitration Rules and Mediation Procedures in effect on
        the date of the request. If there are no Consumer Arbitration Rules and
        Mediation Procedures currently in effect, then the AAA's Commercial
        Arbitration Rules and Mediation Procedure in effect on the date of such
        request shall be utilized. Any party who will be relying upon an expert
        report at the mediation shall provide the mediator and the other parties
        with a copy of the reports.
      </p>
      <p>
        If the Dispute is not fully resolved by mediation, the Dispute shall be
        submitted to binding arbitration and administered by the AAA in
        accordance with the AAA's Consumer Arbitration Rules and Mediation
        Procedures in effect on the date of the request. If there are no
        Consumer Arbitration Rules and Mediation Procedures currently in effect,
        then the AAA's Commercial Arbitration Rules and Mediation Procedure in
        effect on the date of such request shall be utilized. Any judgment upon
        the award rendered by the arbitrator may be entered in and enforced by
        any court having jurisdiction over such Dispute. If the claimed amount
        exceeds $250,000.00 or includes a demand for punitive damages, the
        Dispute shall be heard and determined by three arbitrators; however, if
        mutually agreed to by the parties, then the Dispute shall be heard and
        determined by one arbitrator. Arbitrators shall have expertise in the
        area(s) of Dispute, which may include legal expertise if legal issues
        are involved. All decisions respecting the arbitrability of any Dispute
        shall be decided by the arbitrator(s). At the request of any party, the
        award of the arbitrator(s) shall be accompanied by detailed written
        findings of fact and conclusions of law. Except as may be required by
        law or for confirmation of an award, neither a party nor an arbitrator
        may disclose the existence, content, or results of any arbitration
        hereunder without the prior written consent of both parties.
      </p>
      <p>
        The waiver or invalidity of any portion of this Agreement shall not
        affect the validity or enforceability of the remaining portions of the
        Agreement. The parties further agree (1) that any Dispute involving the
        Company’s affiliates, directors, officers, employees and agents shall
        also be subject to mediation and arbitration as set forth herein, and
        shall not be pursued in a court of law or equity; (2) that the Company
        may, at its sole election, include the Company’s contractors,
        subcontractors and suppliers, as well as any warranty company and
        insurer as parties in the mediation and arbitration; and (3) that the
        mediation and arbitration will be limited to the parties specified
        herein.
      </p>
      <p>
        To the fullest extent permitted by applicable law, the parties agree
        that no finding or stipulation of fact, no conclusion of law, and no
        arbitration award in any other arbitration, judicial, or similar
        proceeding shall be given preclusive or collateral estoppel effect in
        any arbitration hereunder unless there is mutuality of parties. In
        addition, the parties further agree that no finding or stipulation of
        fact, no conclusion of law, and no arbitration award in any arbitration
        hereunder shall be given preclusive or collateral estoppel effect in any
        other arbitration, judicial, or similar proceeding unless there is
        mutuality of parties.
      </p>
      <Heading level={3}>Costs</Heading>
      <p>
        Unless otherwise recoverable by law or statute, each party shall bear
        its own costs and expenses, including attorneys' fees and
        paraprofessional fees, for any mediation and arbitration.
        Notwithstanding the foregoing, if a party unsuccessfully contests the
        validity or scope of arbitration in a court of law or equity, the
        noncontesting party shall be awarded reasonable attorneys' fees,
        paraprofessional fees and expenses incurred in defending such contest,
        including such fees and costs associated with any appellate proceedings.
        In addition, if a party fails to abide by the terms of a mediation
        settlement or arbitration award, the other party shall be awarded
        reasonable attorneys' fees, paraprofessional fees and expenses incurred
        in enforcing such settlement or award.
      </p>
      <Heading level={3}>Accessing Arbitration Rules</Heading>
      <p>
        Buyer may obtain additional information concerning the rules of the AAA
        by visiting its website at www.adr.org or by writing the AAA at 335
        Madison Avenue, New York, New York 10017.
      </p>
      <Heading level={3}>Consumer Due Process and Small Claims</Heading>
      <p>
        Seller supports the principles set forth in the Consumer Due Process
        Protocol developed by the National Consumer Dispute Advisory Committee
        and agrees to the following:
      </p>
      <p>
        Notwithstanding the requirements of arbitration otherwise outlined in
        this Agreement, You shall have the option, after pursuing mediation as
        provided herein, to seek relief in a small claims court for disputes or
        claims within the scope of the court's jurisdiction in lieu of
        proceeding to arbitration. This option does not apply to any appeal from
        a decision by a small claims court. Any mediator and associated
        administrative fees incurred shall be shared equally by the parties;
        however, the parties each agree to pay for their own attorneys' fees and
        costs.
      </p>
      <p>
        Notwithstanding the foregoing, if either party seeks injunctive relief,
        and not monetary damages, from a court because irreparable damage or
        harm would otherwise be suffered by either party before mediation or
        arbitration could be conducted, such actions shall not be interpreted to
        indicate that either party has waived the right to mediate or arbitrate.
        The right to mediate and arbitrate should also not be considered waived
        by the filing of a counterclaim by either party once a claim for
        injunctive relief had been filed with a court.
      </p>
      <Heading level={3}>CLASS ACTION WAIVER</Heading>
      <p>
        THE PARTIES AGREE THAT EACH PARTY MAY BRING CLAIMS AGAINST THE OTHER
        ONLY ON AN INDIVIDUAL BASIS AND NOT AS A MEMBER IN ANY PURPORTED CLASS
        OR REPRESENTATIVE ACTION OR COLLECTIVE PROCEEDING. THE ARBITRATOR(S) MAY
        NOT CONSOLIDATE OR JOIN CLAIMS REGARDING MORE THAN ONE PROPERTY AND MAY
        NOT OTHERWISE PRESIDE OVER ANY FORM OF A CONSOLIDATED, REPRESENTATIVE,
        OR CLASS PROCEEDING. ALSO, THE ARBITRATOR(S) MAY AWARD RELIEF (INCLUDING
        MONETARY, INJUNCTIVE, AND DECLARATORY RELIEF) ONLY IN FAVOR OF THE
        INDIVIDUAL PARTY SEEKING RELIEF AND ONLY TO THE EXTENT NECESSARY TO
        PROVIDE RELIEF NECESSITATED BY THAT PARTY'S INDIVIDUAL CLAIM(S). ANY
        RELIEF AWARDED CANNOT BE AWARDED ON CLASS-WIDE OR MASS-PARTY BASIS OR
        OTHERWISE AFFECT PARTIES WHO ARE NOT A PARTY TO THE ARBITRATION. NOTHING
        IN THE FOREGOING PREVENTS THE COMPANY FROM EXERCISING ITS RIGHT TO
        INCLUDE IN THE MEDIATION AND ARBITRATION contractors, subcontractors and
        suppliers, as well as any warranty company and insurer as parties AS
        DESCRIBED ABOVE.
      </p>
      <Heading level={2} id="limitation-on-time-to-file-claims">
        <Link to="." hash="limitation-on-time-to-file-claims">
          Limitation on Time to File Claims
        </Link>
      </Heading>
      <p>
        ANY CAUSE OF ACTION OR CLAIM YOU MAY HAVE ARISING OUT OF OR RELATING TO
        THESE TERMS OF USE OR THE WEBSITE MUST BE COMMENCED WITHIN ONE (1) YEAR
        AFTER THE CAUSE OF ACTION ACCRUES; OTHERWISE, SUCH CAUSE OF ACTION OR
        CLAIM IS PERMANENTLY BARRED.
      </p>
      <Heading level={2} id="waiver-and-severability">
        <Link to="." hash="waiver-and-severability">
          Waiver and Severability
        </Link>
      </Heading>
      <p>
        No waiver by the Company of any term or condition set out in these Terms
        of Use shall be deemed a further or continuing waiver of such term or
        condition or a waiver of any other term or condition, and any failure of
        the Company to assert a right or provision under these Terms of Use
        shall not constitute a waiver of such right or provision.
      </p>
      <p>
        If any provision of these Terms of Use is held by a court or other
        tribunal of competent jurisdiction to be invalid, illegal, or
        unenforceable for any reason, such provision shall be eliminated or
        limited to the minimum extent such that the remaining provisions of the
        Terms of Use will continue in full force and effect.
      </p>
      <Heading level={2} id="entire-agreement">
        <Link to="." hash="entire-agreement">
          Entire Agreement
        </Link>
      </Heading>
      <p>
        The Terms of Use and our Privacy Policy constitute the sole and entire
        agreement between You and Inflow, Inc. regarding the Website and
        supersede all prior and contemporaneous understandings, agreements,
        representations, and warranties, both written and oral, regarding the
        Website.
      </p>
      <Heading level={2}>Your Comments and Concerns</Heading>
      <address>
        This website is operated by Inflow, Inc, 275 West Wisconsin Avenue,
        Suite 140 Milwaukee, Wisconsin 53203.
      </address>
      <p>
        All other feedback, comments, requests for technical support, and other
        communications relating to the Website should be directed to:{" "}
        <a href="mailto:info@inflowsystems.com">info@inflowsystems.com</a>.
      </p>
    </div>
  )
}
